import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { History } from 'history';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { Document } from 'features/Documents/documents';
import { axiosInstance } from '../axiosInstance';

export type ReportTemplateField = {
  description: string;
  key: string;
  measure: string;
  optional: boolean;
  value: string | number | boolean;
  value_type: string;
};

export type ReportTemplateFormProps = {
  description: string;
  fields: ReportTemplateField[];
  link_type?: string;
  name: string;
};

export type ReportTemplate = {
  author_full_name: string;
  author_id: string;
  can_edit: boolean;
  created_at: string;
  description: string;
  documents: Document[];
  editor_full_name: string;
  editor_id: string;
  fields: ReportTemplateField[];
  id: string;
  link_type: string;
  name: string;
  updated_at: string;
  version: number;
  versions: string | null;
};

type ReportsState = {
  apiErrors: ApiErrors;
  list: {
    [key: string]: ReportTemplate;
  };
  loaders: Loaders;
  saved?: boolean;
  template?: ReportTemplate | undefined;
};

const templatesApiPrefix = '/datasheets/templates';

// TODO: use object instead of  array
const initialState: ReportsState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  list: {},
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  }
};

export const fetchReportTemplates = createAsyncThunk<ReportTemplate[], null, { rejectValue: string }>(
  'reportTemplates/fetchReportTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(templatesApiPrefix);
      const {
        data: { templates }
      } = response;
      return templates;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const fetchTemplate = createAsyncThunk<ReportTemplate, string, { rejectValue: string }>(
  'reportTemplates/fetchTemplate',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${templatesApiPrefix}/${reportId}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteReportTemplate = createAsyncThunk<string, string, { rejectValue: string }>(
  'reportTemplates/deleteReportTemplate',
  async (reportId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id: reportId, value: true }));
      await axiosInstance.delete(`${templatesApiPrefix}/${reportId}`);
      return reportId;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('reportTemplates.you cannot delete this report'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('reportTemplates.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id: reportId, value: false }));
    }
  }
);

interface SaveTemplateParams {
  history: History;
  id?: string;
  template: ReportTemplateFormProps;
}

export const saveTemplate = createAsyncThunk<ReportTemplate, SaveTemplateParams, { rejectValue: string }>(
  'reportTemplates/saveTemplate',
  async ({ template, id, history }, { dispatch, rejectWithValue }) => {
    let response;
    try {
      if (!id) {
        response = await axiosInstance.post(`${templatesApiPrefix}`, template);
      } else {
        response = await axiosInstance.put(`${templatesApiPrefix}/${id}`, template);
      }
      const { data } = response;
      if (!id) {
        history?.replace(`/datasheets/templates/edit/${data.id}`);
      }
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('reportTemplates.you cannot update or create site'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const reportsSlice = createSlice({
  initialState,
  name: 'reports',
  reducers: {
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetReportTemplates: state => {
      state = initialState;
    },
    resetReportTemplate: state => {
      state.template = undefined;
    },
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchReportTemplates.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.list = keyBy(payload, 'id');
      })
      .addCase(fetchReportTemplates.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchReportTemplates.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(fetchTemplate.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.template = payload;
      })
      .addCase(fetchTemplate.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchTemplate.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteReportTemplate.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
      .addCase(saveTemplate.fulfilled, (state, _) => {
        state.loaders.isSaving = false;
      })
      .addCase(saveTemplate.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveTemplate.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = reportsSlice;
export { reducer as reportTemplates };
export const { closeError, updateListDelete, resetReportTemplates, resetReportTemplate } = actions;

export const getReportTemplates = (state: RootState) => state.reportTemplates.list;
export const getTemplate = (state: RootState) => state.reportTemplates.template;
export const getReportTemplatesLoaders = (state: RootState) => state.reportTemplates.loaders;
export const getReportTemplatesApiErrors = (state: RootState) => state.reportTemplates.apiErrors;
export const isSaved = (state: RootState) => state.reportTemplates.saved;
